import { motion } from 'framer-motion';
import PlayingCard from 'components/PlayingCard';
import WinnerRibbonBanner from 'components/WinnerRibbonBanner';
import SeatBetMarker from 'components/SeatBetMarker';
import TurnTimer from 'components/TurnTimer';
import HeroChatPopover from 'components/table/HeroChatPopover';
import type { SeatChatBubbleItem } from 'components/table/TableSeatChatBubbles';
import { PublicPlayerView } from 'types/game';
import type { SeatPositionId } from 'constants/seatLayout';
import { betChipSide, isTopRailSeat } from 'constants/seatLayout';
import { avatarColor, avatarInitial } from 'lib/avatar';
import { formatSeatPlayerName } from 'lib/currency-format';
import { stickerAnimationClass } from 'constants/game';
import { useCurrency } from 'hooks/useCurrency';

const WIN_EASE = [0.22, 1, 0.36, 1] as const;

function SeatWinInline({
  title,
  amount,
  formatAmount,
}: {
  title: string;
  amount: number;
  formatAmount: (value: number, opts?: { maximumFractionDigits?: number }) => string;
}) {
  return (
    <motion.div
      className="tp-seat-win-inline"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: WIN_EASE }}
    >
      <motion.p
        className="tp-seat-win-inline-title"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.06, duration: 0.32, ease: WIN_EASE }}
      >
        {title}
      </motion.p>
      <motion.p
        className="tp-seat-win-inline-amount tabular-nums"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.36, type: 'spring', stiffness: 340, damping: 24 }}
      >
        + {formatAmount(amount, { maximumFractionDigits: 0 })}
      </motion.p>
    </motion.div>
  );
}

function WinnerRibbonFlag({ isHero }: { isHero: boolean }) {
  return (
    <div className="tp-octro-winner-ribbon-wrap absolute left-1/2 z-[35]">
      <motion.div
        initial={{ opacity: 0, scale: 0.72, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.52, ease: WIN_EASE }}
      >
        <div className="tp-winner-banner-wind">
          <WinnerRibbonBanner width={isHero ? 128 : 118} />
        </div>
      </motion.div>
    </div>
  );
}

export type SeatSlot = 'hero' | 'opponent';

interface PlayerSeatProps {
  player: PublicPlayerView;
  slot: SeatSlot;
  isMe?: boolean;
  turnSeconds?: number | null;
  turnMaxSeconds?: number;
  onSee?: () => void;
  showSeeButton?: boolean;
  hideCards?: boolean;
  hideTableCards?: boolean;
  roundKey?: number;
  justRevealed?: boolean;
  cardAnimate?: 'deal' | 'flip' | 'settle' | 'none';
  isWinner?: boolean;
  showCrown?: boolean;
  /** Shown beside winner after pot collect — not at table center */
  wonAmount?: number;
  packedByTimeout?: boolean;
  variant?: 'mobile' | 'desktop';
  isTurn?: boolean;
  turnTimerKey?: string;
  /** Show countdown pill on name row instead of avatar ring (landscape mobile) */
  inlineTurnTimer?: boolean;
  seatId?: SeatPositionId;
  feltSeatNumber?: number;
  currentBet?: number;
  betHighlight?: boolean;
  chatOpen?: boolean;
  onChatToggle?: () => void;
  onChatClose?: () => void;
  onSendChatPhrase?: (phraseId: string) => void;
  onSendChatSticker?: (stickerId: string) => void;
  seatChatBubble?: SeatChatBubbleItem | null;
  /** Latest Blind / Chaal / Show flash — Octro banner on avatar */
  actionFlash?: string | null;
  /** Bumps when a new action flash starts — restarts CSS animation */
  actionFlashKey?: number;
  /** Showdown win line at this seat (portrait Octro) */
  seatWinAnnounce?: { title: string; amount: number } | null;
  /** Hand rank during show / winner (e.g. Sequence) */
  handLabel?: string | null;
}

function statusPill(label: string, cls: string, hidden = false) {
  return (
    <span
      className={`tp-status-pill inline-flex items-center justify-center rounded px-2 py-0.5 text-[7px] font-extrabold uppercase leading-none tracking-[0.08em] sm:text-[8px] ${cls} ${hidden ? 'tp-status-pill-hidden' : ''}`}
      aria-hidden={hidden}
    >
      {label}
    </span>
  );
}

function resolveStatusPill(
  player: PublicPlayerView,
  folded: boolean,
  packedByTimeout: boolean | undefined
): { label: string; cls: string } | null {
  if (folded) {
    return {
      label: packedByTimeout ? 'Timed Out' : 'Packed',
      cls: packedByTimeout ? 'tp-status-timeout bg-orange-800/90 text-white' : 'tp-status-packed bg-red-900/90 text-white',
    };
  }
  if (player.isSeen) {
    return { label: 'Seen', cls: 'tp-status-seen' };
  }
  if (!player.isSeen) {
    return { label: 'Blind', cls: 'tp-status-blind' };
  }
  return null;
}

export default function PlayerSeat({
  player,
  slot,
  isMe,
  turnSeconds,
  turnMaxSeconds = 15,
  onSee,
  showSeeButton,
  hideCards,
  roundKey = 0,
  justRevealed,
  cardAnimate = 'settle',
  isWinner,
  showCrown,
  wonAmount,
  packedByTimeout,
  variant = 'desktop',
  isTurn: isTurnOverride,
  turnTimerKey,
  inlineTurnTimer = false,
  seatId,
  currentBet = 0,
  betHighlight = false,
  chatOpen = false,
  onChatToggle,
  onChatClose,
  onSendChatPhrase,
  onSendChatSticker,
  seatChatBubble = null,
  actionFlash = null,
  actionFlashKey = 0,
  seatWinAnnounce = null,
  handLabel = null,
}: PlayerSeatProps) {
  const { format, formatCompact, formatGrouped } = useCurrency();
  const folded = player.status === 'folded';
  const isHero = slot === 'hero';
  const isDesktop = variant === 'desktop';
  const isActive = isTurnOverride ?? player.isTurn;
  const status = resolveStatusPill(player, folded, packedByTimeout);
  const showCardsOnSeat = !hideCards;
  const cards = showCardsOnSeat ? (player.cards || []).slice(0, 3) : [];
  const hasSeatCards = cards.length > 0;
  const resolvedSeatId = seatId ?? (isHero ? 'hero' : 'bottomLeft');
  const chipSide = betChipSide(resolvedSeatId);
  const isTopRail = isTopRailSeat(resolvedSeatId);

  const avatarPx = isHero ? (isDesktop ? 40 : 36) : isDesktop ? 34 : 32;

  const balanceValue = Math.max(0, Math.floor(Number(player.stack) || 0));
  const displayName = isHero
    ? formatSeatPlayerName(player.username, false)
    : formatSeatPlayerName(player.username, Boolean(isMe));
  const displayBalance = isHero ? formatGrouped(balanceValue) : formatCompact(balanceValue);
  const amountIsStatus = folded || packedByTimeout;
  const amountText = packedByTimeout ? 'Timed Out' : folded ? 'Packed' : displayBalance;
  const fullBalanceLabel = format(balanceValue, { maximumFractionDigits: 0 });
  const balanceTier = isHero
    ? displayBalance.length > 12
      ? 'xwide'
      : displayBalance.length > 8
        ? 'wide'
        : 'default'
    : displayBalance.length > 8
      ? 'xwide'
      : displayBalance.length > 5
        ? 'wide'
        : 'default';
  const balanceCapsuleClass =
    balanceTier === 'xwide'
      ? 'tp-hero-capsule-balance-xwide'
      : balanceTier === 'wide'
        ? 'tp-hero-capsule-balance-wide'
        : '';

  const winnerActive = Boolean(showCrown || isWinner);
  const winnerLit = winnerActive;
  const displayStatus = status;
  const showTimer = isActive && turnSeconds != null && turnSeconds >= 0;
  const showAvatarRingTimer = showTimer && !inlineTurnTimer;
  const showInlineNameTimer = showTimer && inlineTurnTimer;
  const timerUrgent = showInlineNameTimer && (turnSeconds ?? 0) <= 5;
  const timerRingSize = avatarPx + 12;

  const avatarBlock = (
    <div
      className="tp-hero-bottom-avatar relative shrink-0 overflow-visible"
      style={
        {
          '--tp-avatar-size': `${avatarPx}px`,
          '--tp-timer-ring-size': `${timerRingSize}px`,
        } as React.CSSProperties
      }
    >
      {actionFlash ? (
        <div
          key={actionFlashKey}
          className="tp-octro-action-flash tp-octro-action-flash-above-avatar"
          aria-live="polite"
        >
          {actionFlash}
        </div>
      ) : null}
      {winnerActive ? (
        <div className="tp-winner-avatar-glow pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2" aria-hidden>
          <span className="tp-winner-avatar-glow-halo" />
          <span className="tp-winner-avatar-glow-ring" />
        </div>
      ) : null}
      {showCrown ? <WinnerRibbonFlag isHero={isHero} /> : null}
      {wonAmount != null && wonAmount > 0 && (
        <motion.div
          className="tp-seat-won-amount absolute left-1/2 z-[36] -translate-x-1/2"
          style={{ top: showCrown ? '-3.25rem' : '-2.75rem' }}
          initial={{ opacity: 0, scale: 0.92, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.42, ease: WIN_EASE }}
        >
          <motion.span
            className="tp-seat-won-label"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.32, ease: WIN_EASE }}
          >
            Won
          </motion.span>
          <motion.span
            className="tp-seat-won-value tabular-nums"
            initial={{ opacity: 0, y: 4, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.22, duration: 0.38, type: 'spring', stiffness: 340, damping: 22 }}
          >
            {format(wonAmount, { maximumFractionDigits: 0 })}
          </motion.span>
        </motion.div>
      )}
      {isActive && isDesktop && !showAvatarRingTimer && (
        <div className="tp-spotlight-ring absolute -inset-1 rounded-full" aria-hidden />
      )}
      <div
        className={`tp-avatar-ring-wrap absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 ${
          showAvatarRingTimer ? 'tp-avatar-ring-wrap-timer' : ''
        }`}
      >
        <div
          className={`tp-avatar-ring relative h-full w-full ${
            isActive && isDesktop
              ? showTimer
                ? 'tp-avatar-ring-turn'
                : 'tp-avatar-ring-active'
              : ''
          } ${showAvatarRingTimer ? 'tp-avatar-ring-no-pad' : ''}`}
        >
          <div
            className="tp-avatar-inner relative flex h-full w-full items-center justify-center overflow-hidden rounded-full text-xs font-bold text-white sm:text-sm"
            style={{ background: avatarColor(player.username) }}
          >
            {avatarInitial(player.username)}
            {displayStatus != null &&
            !actionFlash &&
            (displayStatus.label === 'Blind' || displayStatus.label === 'Seen') ? (
              <span className={`tp-avatar-status-banner tp-avatar-status-${displayStatus.label.toLowerCase()}`}>
                {displayStatus.label}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {showAvatarRingTimer && (
        <TurnTimer
          key={turnTimerKey}
          syncKey={turnTimerKey}
          secondsLeft={turnSeconds}
          maxSeconds={turnMaxSeconds}
          active
          size={timerRingSize}
          variant="ring"
          className="tp-avatar-ring-timer"
        />
      )}
    </div>
  );

  const isMobile = variant === 'mobile';
  const useStackedBet = isMobile;
  const useCompactBet = (isMobile || inlineTurnTimer) && !useStackedBet;

  const betMarker =
    currentBet > 0 ? (
      <SeatBetMarker
        amount={currentBet}
        highlight={betHighlight}
        inline
        compact={useCompactBet}
        stacked={useStackedBet}
      />
    ) : null;

  const fanIndexFor = (index: number, total: number) => {
    if (total <= 1) return 1;
    if (total === 2) return index === 0 ? 0 : 2;
    return index;
  };

  const allFaceUp = cards.length > 0 && cards.every((c) => !c.hidden);

  const cardFan = cards.map((c, i) => (
    <PlayingCard
      key={`${roundKey}-${i}-${c.code}`}
      code={c.code}
      hidden={c.hidden}
      compact
      large={!isMobile && isHero}
      medium={isMobile || !isHero}
      fanIndex={fanIndexFor(i, cards.length)}
      roundKey={roundKey}
      delay={i * 0.06}
      animate={
        justRevealed && isMe && !c.hidden
          ? 'flip'
          : cardAnimate === 'deal'
            ? cardAnimate
            : 'none'
      }
    />
  ));

  const betSlot = betMarker ? <div className="tp-seat-bet-inline-slot">{betMarker}</div> : null;

  const cardsLayer = showCardsOnSeat ? (
    <div
      className={`tp-player-cards-cluster tp-cards-fan-row${allFaceUp ? ' tp-cards-open-hand' : ''}${isMobile ? ' tp-player-cards-cluster-mobile' : ''} ${isHero ? 'tp-hero-cards-row' : 'tp-opponent-cards-row'}${folded ? ' tp-cards-packed' : ''}`}
    >
      {chipSide === 'left' && betSlot}
      <div className="tp-player-cards-inner tp-cards-mobile-fan tp-cards-classic-fan relative">
        {cardFan}
        {isHero && showSeeButton && onSee ? (
          <button type="button" onClick={onSee} className="tp-octro-see">
            SEE
          </button>
        ) : null}
      </div>
      {chipSide === 'right' && betSlot}
    </div>
  ) : null;

  const dealerBadge = player.isDealer ? (
    <span className="tp-dealer-btn-outside tp-dealer-inline shrink-0" title="Dealer">
      D
    </span>
  ) : null;

  const isLeftRailSeat = resolvedSeatId === 'topLeft' || resolvedSeatId === 'bottomLeft';
  const opponentCardsLayer = !isHero ? (
    <div
      className={`tp-player-cards-layer tp-player-cards-row tp-seat-cards-${resolvedSeatId}${hasSeatCards ? '' : ' tp-player-layer-empty'}${allFaceUp ? ' tp-player-cards-open' : ''}`}
      aria-hidden={!hasSeatCards}
    >
      {cardsLayer}
    </div>
  ) : null;

  const playerCapsule = (
    <div
      className={`tp-hero-bottom-capsule relative flex items-center ${balanceCapsuleClass} ${isActive && isDesktop ? 'tp-hero-bottom-capsule-active' : ''} ${isActive && !isDesktop ? 'tp-hero-bottom-capsule-turn-mobile' : ''} ${winnerActive ? 'tp-hero-capsule-winner' : ''} ${folded ? 'tp-seat-folded opacity-80' : ''} ${!isHero ? 'tp-player-capsule-opponent' : ''} ${isHero && isMe ? 'tp-hero-capsule-you' : ''}`}
    >
      {folded && <div className="tp-fold-overlay pointer-events-none absolute inset-0 z-20 rounded-full" aria-hidden />}
      {!isHero ? (
        <div
          className={`tp-side-avatar-card-rail ${isLeftRailSeat ? 'tp-side-rail-left' : 'tp-side-rail-right'}`}
        >
          {avatarBlock}
          {opponentCardsLayer}
        </div>
      ) : (
        <>
          {avatarBlock}
          {showCardsOnSeat ? (
            <div
              className={`tp-hero-card-slot${hasSeatCards ? '' : ' tp-hero-card-slot-empty'}${allFaceUp ? ' tp-hero-card-slot-open' : ''}`}
              aria-hidden={!hasSeatCards}
            >
              {cardsLayer}
            </div>
          ) : null}
        </>
      )}
      <div className={`tp-hero-bottom-info relative min-w-0 overflow-visible ${isHero ? 'shrink-0' : 'shrink'}`}>
        <div
          className={`tp-hero-bottom-info-core min-w-0${!isHero ? ` tp-octro-opponent-nameplate tp-octro-opponent-nameplate-${resolvedSeatId}` : ''}`}
        >
          <div className="tp-octro-name-dealer-wrap">
            {!isHero &&
            dealerBadge &&
            (resolvedSeatId === 'bottomRight' || resolvedSeatId === 'topRight') ? (
              <div className="tp-seat-dealer-inline">{dealerBadge}</div>
            ) : null}
            <div className="tp-octro-nameplate-body min-w-0">
              <div className="tp-hero-bottom-name-row flex min-w-0 items-center gap-0.5">
                <p
                  className="tp-hero-bottom-name min-w-0 truncate leading-tight"
                  title={isMe ? 'you' : player.username}
                >
                  {displayName}
                </p>
                {showInlineNameTimer ? (
                  <span
                    className={`tp-name-turn-timer shrink-0 ${timerUrgent ? 'tp-name-turn-timer-urgent' : ''}`}
                    aria-label={`${Math.max(0, Math.ceil(turnSeconds ?? 0))} seconds left`}
                  >
                    {Math.max(0, Math.ceil(turnSeconds ?? 0))}
                  </span>
                ) : null}
              </div>
              {seatWinAnnounce ? (
                <SeatWinInline
                  title={seatWinAnnounce.title}
                  amount={seatWinAnnounce.amount}
                  formatAmount={format}
                />
              ) : handLabel ? (
                <p className="tp-octro-hand-label">{handLabel}</p>
              ) : null}
              <p
                className={`tp-hero-bottom-balance tp-octro-amount font-display font-bold tabular-nums leading-none ${
                  isHero ? 'tp-octro-amount-full' : ''
                } ${
                  amountIsStatus ? 'tp-octro-amount-status' : ''
                } ${packedByTimeout ? 'tp-octro-amount-timeout' : ''} ${folded && !packedByTimeout ? 'tp-octro-amount-packed' : ''}`}
                title={amountIsStatus ? amountText : fullBalanceLabel}
              >
                {amountIsStatus ? null : <span className="tp-octro-amount-icon" aria-hidden />}
                {amountText}
              </p>
              <div className="tp-hero-bottom-status" aria-hidden={displayStatus == null || displayStatus.label === 'Blind' || displayStatus.label === 'Seen' || displayStatus.label === 'Packed' || displayStatus.label === 'Timed Out'}>
                {displayStatus != null && displayStatus.label !== 'Blind' && displayStatus.label !== 'Seen' && displayStatus.label !== 'Packed' && displayStatus.label !== 'Timed Out'
                  ? statusPill(displayStatus.label, displayStatus.cls)
                  : statusPill('', 'tp-status-pill-hidden', true)}
              </div>
            </div>
            {!isHero &&
            dealerBadge &&
            (resolvedSeatId === 'bottomLeft' || resolvedSeatId === 'topLeft') ? (
              <div className="tp-seat-dealer-inline">{dealerBadge}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`tp-player-seat-root tp-player-seat-${resolvedSeatId} tp-player-float tp-unified-player-seat relative overflow-visible ${isHero ? 'tp-hero-seat-compact' : 'tp-opponent-seat-unified'} ${isTopRail ? 'tp-opponent-hud-top' : ''} ${resolvedSeatId === 'topRight' ? 'tp-opponent-hud-top-right' : ''} ${resolvedSeatId === 'topLeft' ? 'tp-opponent-hud-top-left' : ''} ${isActive && isDesktop ? 'tp-seat-active' : 'tp-seat-idle'} ${isActive && !isDesktop ? 'tp-seat-turn-mobile' : ''} ${folded ? 'tp-seat-folded' : ''} ${winnerLit ? 'tp-winner-glow' : ''} ${hasSeatCards ? '' : 'tp-seat-no-cards'}`}
    >
      <div className="tp-player-seat-stack">
        <div className={`tp-player-hud-column ${isHero ? 'tp-player-hud-column-hero' : ''}`}>
          <div className={`tp-player-hud-row ${isHero ? 'tp-player-hud-row-hero' : ''}`}>
            <div className="tp-player-hud-center">
              {seatChatBubble && (
                <div className="tp-seat-table-bubble-wrap" aria-live="polite">
                  <div
                    className={`tp-seat-table-bubble ${
                      seatChatBubble.kind === 'sticker' ? 'tp-seat-table-bubble-sticker' : ''
                    } ${seatChatBubble.kind === 'sticker' ? stickerAnimationClass(seatChatBubble.stickerId) : ''}`}
                  >
                    {seatChatBubble.text}
                  </div>
                </div>
              )}
              <div className="tp-hero-hud tp-hero-bottom-hud shrink-0">
                {playerCapsule}
              </div>
            </div>
            {isHero && dealerBadge && (
              <div className="tp-player-hud-addon tp-player-hud-dealer">{dealerBadge}</div>
            )}
            {isMe && onChatToggle && onChatClose && onSendChatPhrase && onSendChatSticker && (
              <div className="tp-player-hud-addon tp-player-hud-chat">
                <HeroChatPopover
                  open={chatOpen}
                  onToggle={onChatToggle}
                  onClose={onChatClose}
                  onSendPhrase={onSendChatPhrase}
                  onSendSticker={onSendChatSticker}
                />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
